import type { GeoPosition } from './RideProgressService';

export type GeofenceCallback = (position: GeoPosition) => void;

export class GeofenceService {
  private watchId: number | null = null;
  private subscribers = new Map<GeofenceCallback, PositionOptions | undefined>();
  private lastOptions: PositionOptions = {
    enableHighAccuracy: true,
    maximumAge: 5000,
    timeout: 15000,
  };

  /** Subscribe to GPS updates. Stops the watch when the last subscriber unsubscribes. */
  subscribe(callback: GeofenceCallback, options?: PositionOptions): () => void {
    this.subscribers.set(callback, options);
    this.restartWatch();
    return () => {
      this.subscribers.delete(callback);
      if (this.subscribers.size === 0) {
        this.stopInternal();
      } else {
        this.restartWatch();
      }
    };
  }

  /** @deprecated Prefer subscribe() for shared GPS watches. */
  start(callback: GeofenceCallback, options?: PositionOptions): void {
    this.stopInternal();
    this.subscribers.clear();
    this.subscribers.set(callback, options);
    this.restartWatch();
  }

  /** @deprecated Prefer the unsubscribe returned from subscribe(). */
  stop(): void {
    this.subscribers.clear();
    this.stopInternal();
  }

  private restartWatch(): void {
    this.stopInternal();
    if (this.subscribers.size === 0 || !('geolocation' in navigator)) return;

    const merged: PositionOptions = { ...this.lastOptions };
    for (const opts of this.subscribers.values()) {
      if (opts) Object.assign(merged, opts);
    }

    this.watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const position: GeoPosition = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracyM: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
        };
        for (const callback of this.subscribers.keys()) {
          callback(position);
        }
      },
      () => {
        // Permission denied or unavailable - caller handles via separate UX.
      },
      merged,
    );
  }

  private stopInternal(): void {
    if (this.watchId != null && 'geolocation' in navigator) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  /** One-shot GPS read used when the app returns to foreground. */
  getCurrentPosition(): Promise<GeoPosition> {
    return new Promise((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        reject(new Error('GPS unavailable'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracyM: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
          }),
        (err) => reject(err),
        { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 },
      );
    });
  }
}

export const geofenceService = new GeofenceService();
